/** Ce que le navigateur envoie au suivi d'erreurs, débarrassé de ce qui n'a
 * rien à y faire.
 *
 * 🔑 Ce module existe séparément de `index.jsx` pour UNE raison : pouvoir être
 * éprouvé. Importer `index.jsx` monterait l'application entière, si bien que
 * la fonction ne pouvait se relire qu'en la recopiant à la main — ce qui a été
 * fait deux fois le 9 septembre 2026, et ne prouve rien sur le code livré.
 * Voir `masquer-evenement.test.js`.
 */

// ⛔ Ce que le navigateur envoie au suivi d'erreurs, débarrassé de ce qui
// n'a rien à y faire.
//
// La page de réinitialisation lit son code dans l'URL (`queryParams.get('code')`
// dans ResetPasswordVerify.jsx). Une erreur JavaScript sur cette page envoyait
// donc l'URL complète, code compris, à GlitchTip — où elle serait restée
// 90 jours. Le serveur est le nôtre, à Toronto, mais un code de
// réinitialisation n'a pas à traîner dans un outil de débogage.
//
// 🔑 Le même trou a été bouché côté API le 9 septembre 2026, avec le même
// raisonnement : voir `labibli-backend/src/helpers/journal_sentry.py`.
const ADRESSES = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

// Les mots qui, présents dans un NOM, disent que la valeur d'à côté est un
// secret. Comparaison mot à mot et non par sous-chaîne : `auth` doit attraper
// `Authorization` sans attraper `author`, qui est un champ ordinaire de ce
// produit et qu'on veut lire dans une trace.
//
// ⛔ `code` n'y est PAS, délibérément. Il reste dans `PARAMETRE` ci-dessous,
// où la forme `?code=` désigne sans ambiguïté le code de réinitialisation ;
// comme nom de champ il désigne au contraire `ECONNABORTED`, que axios pose
// sur chaque expiration de délai — une des informations les plus utiles des
// alertes du front, qu'un masquage par prudence ferait disparaître.
const MOTS_SENSIBLES = new Set([
  'token', 'tokens', 'auth', 'authorization', 'authorisation', 'authentication',
  'key', 'keys', 'apikey', 'secret', 'password', 'passwd', 'pwd',
  'cookie', 'cookies', 'session', 'sessionid', 'csrf', 'csrftoken',
  'jwt', 'bearer', 'otp', 'signature', 'credential', 'credentials',
  'email', 'mail',
]);

// 🔑 Le nom est DÉCOUPÉ avant d'être comparé : `accessToken`, `user_token`,
// `X-Api-Key` et `Authorization` portent tous le même mot, mais aucun ne
// s'écrit comme lui. Une égalité exacte les laissait tous passer, et
// `Authorization: token …` est la forme exacte de `services/auth-header.js`.
function motsDe(cle) {
  return String(cle)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/);
}

function cleSensible(cle) {
  return motsDe(cle).some((mot) => MOTS_SENSIBLES.has(mot));
}

// 🔑 `code` se juge sur sa VALEUR, seul nom du lot à le faire, parce qu'il en
// désigne deux qui n'ont rien à voir : le code de réinitialisation rendu par
// `authemail` (40 caractères hexadécimaux) et le code d'erreur d'axios
// (`ECONNABORTED`, `ERR_BAD_RESPONSE`), qui est une des informations les plus
// utiles des alertes du front. Les deux critères les séparent proprement : les
// codes d'axios sont courts et TOUJOURS en capitales.
function valeurSecrete(valeur) {
  return typeof valeur === 'string' && valeur.length >= 16 && /[a-z]/.test(valeur);
}

const NOM_CODE = /\b(code[\w-]*)\s*:\s*"?([A-Za-z0-9_-]+)"?/gi;

const NOMS = [...MOTS_SENSIBLES].join('|');

// Forme URL. `[\w-]*` après le nom : `?code_verifier=` portait le code et
// échappait à une alternance sans suffixe, parce que `code` y est suivi d'un
// souligné et non d'un `=`.
const PARAMETRE = new RegExp(`(^|[?&#])((?:code|${NOMS})[\\w-]*)=[^&#\\s]+`, 'gi');

// Forme libre et JSON, pour ce qui arrive en CHAÎNE plutôt qu'en objet :
// `localStorage.getItem('user')` rend `{"token":"…"}`, le jeton de session
// d'une bibliothèque entière, sous une clé `user` qui n'a l'air de rien.
// ⚠️ DEUX familles, et les confondre coûte des deux côtés. Un seul motif
// glouton avalait la prose — `session: expirée — redirection vers /login`
// devenait `session: [retiré]`, et `mail: envoi impossible vers le relais
// Cyberimpact, code 535` perdait tout sauf le numéro. Mais le borner à un seul
// jeton ROUVRE une fuite, parce que `Authorization: token <valeur>` porte sa
// valeur en DEUX jetons : on masquerait `token` et on laisserait le secret.

// Créance structurée : la valeur porte des espaces ou des séparateurs
// (`Authorization: token …`, `Cookie: a=b; c=d`). On avale le segment entier.
const NOMS_GLOUTONS = new Set([
  'auth', 'authorization', 'authorisation', 'cookie', 'cookies', 'bearer',
]);
const CHAMP_GLOUTON = new RegExp(
  `("?)(${[...NOMS_GLOUTONS].join('|')})\\1\\s*:\\s*("?)[^"',;}\\n]+\\3`, 'gi',
);

// Tous les autres apparaissent aussi en PROSE : on ne masque qu'UN jeton,
// précédé au besoin de son schéma — `token: Bearer <valeur>` en porte un.
const NOMS_BORNES = [...MOTS_SENSIBLES].filter((mot) => !NOMS_GLOUTONS.has(mot));
const CHAMP_BORNE = new RegExp(
  `("?)(${NOMS_BORNES.join('|')})\\1\\s*:\\s*("?)(?:(?:bearer|token|basic)\\s+)?[^\\s"',;}]+\\3`,
  'gi',
);

// ⚠️ 12 et non 8, comme le jumeau backend : `exception.values[].stacktrace`
// `.frames[].vars` descend plus bas que huit niveaux, et s'arrêter avant
// laisserait passer en clair précisément ce qu'on cherche à masquer.
const PROFONDEUR_MAX = 12;

function nettoyer(valeur, profondeur = 0) {
  // ⛔ Au-delà de la limite on rend un marqueur, PAS la valeur. Rendre la
  // valeur ferait échouer la protection en l'ouvrant, ce qui est le pire des
  // deux comportements possibles.
  if (profondeur > PROFONDEUR_MAX) return '[trop profond, retiré]';
  if (typeof valeur === 'string') {
    return valeur
      .replace(PARAMETRE, '$1$2=[retiré]')
      .replace(CHAMP_GLOUTON, '$1$2$1: [retiré]')
      .replace(CHAMP_BORNE, '$1$2$1: [retiré]')
      .replace(NOM_CODE, (tout, nom, v) => (valeurSecrete(v) ? `${nom}: [retiré]` : tout))
      .replace(ADRESSES, '[adresse masquée]');
  }
  if (Array.isArray(valeur)) return valeur.map((v) => nettoyer(v, profondeur + 1));
  if (valeur && typeof valeur === 'object') {
    // 🔑 Les CLÉS aussi, comme côté backend. Un objet `{ code: "a1b2…" }` ne
    // porte le secret que dans sa valeur, mais `{ "jeanne@exemple.test": … }`
    // le porte dans sa clé — et c'est la forme d'un dictionnaire indexé par
    // destinataire.
    return Object.fromEntries(
      Object.entries(valeur).map(([c, v]) => {
        const cle = nettoyer(c, profondeur + 1);
        const secrete = cleSensible(c)
          || (motsDe(c).includes('code') && valeurSecrete(v));
        return [cle, secrete ? '[retiré]' : nettoyer(v, profondeur + 1)];
      }),
    );
  }
  return valeur;
}

// ⛔ Export NOMMÉ et pas par défaut : un export par défaut portant aussi un
// nom fait échouer `import/no-named-as-default` chez qui l'importe.
export { nettoyer, cleSensible, valeurSecrete };
